"use client";

import {
  Box,
  createToaster,
  Toast,
  Toaster as ChakraToaster
} from "@chakra-ui/react";

import { useMemo, useRef, type ReactNode } from "react";

import { asSlotComponent } from "../internal/v3-slot";

const ToastRoot = asSlotComponent(Toast.Root);
const ToastTitle = asSlotComponent(Toast.Title);
const ToastDescription = asSlotComponent(Toast.Description);
const ToastCloseTrigger = asSlotComponent(Toast.CloseTrigger);
const ToasterRoot = asSlotComponent(ChakraToaster);

export type ToastStatus = "success" | "error" | "warning" | "info" | "loading";

export interface ToastOptions {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  status?: ToastStatus;
  /** ms; `null` keeps the toast open until dismissed (v2 semantics). */
  duration?: number | null;
  isClosable?: boolean;
  icon?: React.ReactNode;
  onCloseComplete?: () => void;
}

export interface UseToastOptions extends ToastOptions {
  defaultOptions?: ToastOptions;
}

type ToastFn = ((options: ToastOptions) => string | undefined) & {
  isActive: (id: string) => boolean;
  close: (id?: string) => void;
  closeAll: () => void;
  update: (id: string, options: ToastOptions) => void;
};

export const toaster = createToaster({
  placement: "bottom",
  overlap: true,
  gap: 12
});

const PERSISTENT_DURATION = Number.MAX_SAFE_INTEGER;

type RenderedToast = {
  id: string;
  type?: string;
  title?: ReactNode;
  description?: ReactNode;
  closable?: boolean;
  meta?: { icon?: ReactNode };
};

const toMs = (duration: ToastOptions["duration"]): number | undefined => {
  if (duration === null) return PERSISTENT_DURATION;
  if (duration === undefined) return undefined;
  return duration;
};

function mapStatusToType(status?: ToastStatus) {
  return status ?? "info";
}

function buildCreatePayload(
  options: ToastOptions,
  defaults: UseToastOptions
): Parameters<typeof toaster.create>[0] {
  const {
    title,
    description,
    status,
    duration,
    isClosable,
    id,
    icon,
    onCloseComplete
  } = { ...defaults, ...options };

  // zag-js create() assigns a generated id, then spreads `...data` — an explicit
  // `id: undefined` overwrites the generated value and breaks ToastActor.
  return {
    ...(id != null ? { id } : {}),
    title,
    description,
    type: mapStatusToType(status),
    duration: toMs(duration),
    closable: isClosable ?? true,
    meta: icon ? { icon } : undefined,
    onStatusChange: (details) => {
      if (details.status === "unmounted") {
        onCloseComplete?.();
      }
    }
  };
}

function buildUpdatePayload(
  options: ToastOptions,
  defaults: UseToastOptions
): Parameters<typeof toaster.update>[1] {
  const {
    title,
    description,
    status,
    duration,
    isClosable,
    icon,
    onCloseComplete
  } = { ...defaults, ...options };

  return {
    title,
    description,
    type: mapStatusToType(status),
    duration: toMs(duration),
    closable: isClosable ?? true,
    meta: icon ? { icon } : undefined,
    onStatusChange: onCloseComplete
      ? (details) => {
          if (details.status === "unmounted") {
            onCloseComplete();
          }
        }
      : undefined
  };
}

export function AppToaster() {
  return (
    <ToasterRoot toaster={toaster}>
      {(toast: RenderedToast) => (
        <ToastRoot
          key={toast.id}
          type={(toast.type as ToastStatus) ?? "info"}
          data-testid="chakra-toast"
          data-toast-type={toast.type ?? "info"}
          data-toast-title={
            typeof toast.title === "string" ? toast.title : undefined
          }
        >
          {/*
            v2 toasts wrapped the custom icon in AlertIcon
            (w:5 / h:6 / flexShrink:0, matching title lineHeight:6).
            Dropping that wrapper left LuInfo unconstrained and visually
            oversized / misaligned against the title row.
          */}
          {toast.meta?.icon != null && (
            <Box
              flexShrink={0}
              w="5"
              h="6"
              color="white"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              css={{ "& > svg": { width: "1.25rem", height: "1.25rem" } }}
            >
              {toast.meta.icon}
            </Box>
          )}
          {/*
            v2 toast rendered AlertIcon + a flex:1 column wrapping title then
            description, then an absolute close button. Without that column,
            Chakra v3's horizontal Toast.Root lays title and description out
            side-by-side (jumbled text, wrong visual weight). Mirror v2 here.
          */}
          <Box flex="1" maxW="100%" display="flex" flexDirection="column">
            {toast.title != null && <ToastTitle>{toast.title}</ToastTitle>}
            {toast.description != null && (
              <ToastDescription>{toast.description}</ToastDescription>
            )}
          </Box>
          {toast.closable && <ToastCloseTrigger aria-label="Close" />}
        </ToastRoot>
      )}
    </ToasterRoot>
  );
}

function createToastHandler(
  getDefaults: () => UseToastOptions,
  instance = toaster
): ToastFn {
  const toastFn = ((options: ToastOptions = {}) => {
    return instance.create(buildCreatePayload(options, getDefaults()));
  }) as ToastFn;

  toastFn.isActive = (id: string) => instance.isVisible(id);
  toastFn.close = (id?: string) => instance.dismiss(id);
  toastFn.closeAll = () => instance.dismiss();
  toastFn.update = (id: string, options: ToastOptions) => {
    instance.update(id, buildUpdatePayload(options, getDefaults()));
  };

  return toastFn;
}

const EMPTY_TOAST_DEFAULTS: UseToastOptions = {};

// Returns a `ToastFn` with a stable identity (via ref+memo) so `useEffect` deps
// on `toast` don't re-fire when callers pass an inline `defaults` object.
export function useToast(
  defaults: UseToastOptions = EMPTY_TOAST_DEFAULTS
): ToastFn {
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  return useMemo(() => createToastHandler(() => defaultsRef.current), []);
}
